import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Avatar,
  InputAdornment,
  Alert,
  Link,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  LockReset as LockResetIcon,
  Code as CodeIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  Person,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { useToast } from '../ToastProvider/ToastProvider';

interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
  onResetPassword: (username: string, code: string, newPassword: string) => Promise<void>;
  onSendCode: (username: string) => Promise<void>;
}

export function ForgotPasswordModal({
  open,
  onClose,
  onResetPassword,
  onSendCode
}: ForgotPasswordModalProps) {
  const toast = useToast();
  
  const [step, setStep] = useState(0); // 0: Enter username, 1: Enter code & new password, 2: Success
  const [username, setUsername] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [canResend, setCanResend] = useState(true);

  const steps = ['Enter Username', 'Reset Password', 'Complete'];

  const handleClose = () => {
    // Reset all state
    setStep(0);
    setUsername('');
    setConfirmationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setCanResend(true);
    onClose();
  };

  const handleSendCode = async () => {
    if (!username.trim()) {
      toast.showError('Please enter a username');
      return;
    }

    setIsLoading(true);
    try {
      await onSendCode(username);
      toast.showSuccess('Reset code sent to: '+ username);
      setStep(1);
    } catch (error: any) {
      if (error.code === 'InvalidParameterException') {
        toast.showError('Please verify your username before resetting your password. Please contact support for help.');
      } else {
        toast.showError('Failed to send reset code. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;

    setIsLoading(true);
    try {
      await onSendCode(username);
      toast.showSuccess('Reset code resent to your email');
      setCanResend(false);
      
      // Re-enable resend after 60 seconds
      setTimeout(() => {
        setCanResend(true);
      }, 60000);
    } catch (error: any) {
      toast.showError(error.message || 'Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  const validatePassword = () => {
    if (newPassword.length < 8) {
      toast.showError('Password must be at least 8 characters long');
      return false;
    }

    if (newPassword !== confirmPassword) {
      toast.showError('Passwords do not match');
      return false;
    }

    if (!/[A-Z]/.test(newPassword)) {
      toast.showError('Password must contain at least one uppercase letter');
      return false;
    }

    if (!/[a-z]/.test(newPassword)) {
      toast.showError('Password must contain at least one lowercase letter');
      return false;
    }

    if (!/[0-9]/.test(newPassword)) {
      toast.showError('Password must contain at least one number');
      return false;
    }

    return true;
  };

  const handleResetPassword = async () => {
    if (!confirmationCode.trim()) {
      toast.showError('Please enter the confirmation code');
      return;
    }

    if (!validatePassword()) {
      return;
    }

    setIsLoading(true);
    try {
      await onResetPassword(username, confirmationCode, newPassword);
      toast.showSuccess('Password reset successfully');
      setStep(2);
      
      // Auto-close after 3 seconds
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error: any) {
      toast.showError(error.message || 'Failed to reset password. Please check your code and try again.');
      console.error('Error resetting password:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.light' }}>
            <LockResetIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Reset Password
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {step === 0 && 'Enter your username to receive a reset code'}
              {step === 1 && 'Enter the code and your new password'}
              {step === 2 && 'Password reset successful!'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Stepper */}
        <Stepper activeStep={step} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 0: Enter Username */}
        {step === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Alert severity="info">
              Enter your username. We'll send you a code to reset your password. This will be either an email or phone number
            </Alert>

            <TextField
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSendCode();
                }
              }}
            />
          </Box>
        )}

        {/* Step 1: Enter Code and New Password */}
        {step === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Alert severity="info">
              Check your email for the confirmation code. Enter it below along with your new password.
            </Alert>

            <TextField
              fullWidth
              label="Confirmation Code"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              placeholder="Enter the 6-digit code"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CodeIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              helperText="Must be at least 8 characters with uppercase, lowercase, and numbers"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              error={confirmPassword !== '' && newPassword !== confirmPassword}
              helperText={
                confirmPassword !== '' && newPassword !== confirmPassword
                  ? 'Passwords do not match'
                  : ''
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Didn't receive the code?{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={handleResendCode}
                  disabled={!canResend || isLoading}
                  sx={{
                    fontWeight: 600,
                    cursor: canResend ? 'pointer' : 'not-allowed',
                    opacity: canResend ? 1 : 0.5
                  }}
                >
                  {canResend ? 'Resend Code' : 'Code sent (wait 60s)'}
                </Link>
              </Typography>
            </Box>
          </Box>
        )}

        {/* Step 2: Success */}
        {step === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 3 }}>
            <Avatar sx={{ bgcolor: 'success.light', width: 80, height: 80 }}>
              <CheckCircleIcon sx={{ fontSize: 50, color: 'success.main' }} />
            </Avatar>
            
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Password Reset Successful!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your password has been updated. You can now sign in with your new password.
              </Typography>
            </Box>

            <Alert severity="success" sx={{ width: '100%' }}>
              Redirecting to login...
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0 }}>
        {step < 2 && (
          <>
            <Button onClick={handleClose} variant="outlined" disabled={isLoading}>
              Cancel
            </Button>
            
            {step === 0 && (
              <Button
                onClick={handleSendCode}
                variant="contained"
                disabled={isLoading || !username.trim()}
              >
                {isLoading ? 'Sending...' : 'Send Code'}
              </Button>
            )}
            
            {step === 1 && (
              <Button
                onClick={handleResetPassword}
                variant="contained"
                disabled={
                  isLoading ||
                  !confirmationCode.trim() ||
                  !newPassword ||
                  !confirmPassword ||
                  newPassword !== confirmPassword
                }
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            )}
          </>
        )}
        
        {step === 2 && (
          <Button onClick={handleClose} variant="contained" fullWidth>
            Return to Login
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default function SupportContactModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const supportEmail = 'support@stafflows.com';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(supportEmail);
      setCopied(true);
      toast.showSuccess('Email copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.showError('Failed to copy email');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs">

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', py: 1 }}>
          <Typography variant="body1">For support, email:</Typography>
          <Link href={`mailto:${supportEmail}`} underline="hover" sx={{ fontWeight: 600 }}>
            {supportEmail}
          </Link>
          <Typography variant="caption" color="text.secondary">Or copy the address</Typography>
          <Button variant="outlined" onClick={handleCopy} startIcon={<ContentCopyIcon />}>
            {copied ? 'Copied!' : 'Copy Email'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// Example usage showing the integration
export function ForgotPasswordExample() {
  const [showModal, setShowModal] = useState(false);

  const handleSendCode = async (username: string) => {
    // TODO: Replace with actual API call
    console.log('Sending reset code to:', username);
    
    // Example: Call your AWS Cognito forgotPassword function
    // await forgotPasswordBusiness(username);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  const handleResetPassword = async (username: string, code: string, newPassword: string) => {
    // TODO: Replace with actual API call
    console.log('Resetting password for:', username, 'with code:', code);
    
    // Example: Call your AWS Cognito confirmPassword function
    // await confirmPasswordBusiness(username, code, newPassword);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  return (
    <Box sx={{ p: 4 }}>
      <Button variant="contained" onClick={() => setShowModal(true)}>
        Open Forgot Password
      </Button>

      <ForgotPasswordModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSendCode={handleSendCode}
        onResetPassword={handleResetPassword}
      />
    </Box>
  );
}

export function SupportCardsExample() {
  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ p: 4 }}>
      <Button variant="contained" onClick={() => setOpen(true)}>
        Contact Support
      </Button>

      <SupportContactModal open={open} onClose={() => setOpen(false)} />
    </Box>
  );
}